var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React from 'react';
import classnames from 'classnames';
import { handleRef } from '../../utilities/SemanticUtils';
import { isElementOfType } from '../../utilities/isElementOfType';
import { InputAddon } from './InputAddon';
import { Icon } from '../Icon';
import { Spinner } from '../Spinner';
import { Button } from '../Button';
import { FormFieldContext } from '../FormField';
import { splitPropsUsingArray, htmlInputPropsArray, inputPropsArray, } from './inputProps';
const { Children, createElement, createRef, cloneElement, isValidElement } = React;
export class Input extends React.Component {
    constructor() {
        super(...arguments);
        this.inputRef = this.props.inputRef || createRef();
        this.renderAction = () => {
            const { action, disabled } = this.props;
            if (action == null)
                return null;
            if (typeof action === 'string') {
                return (React.createElement(Button, { disabled: disabled, size: this.computeSize().inputSize }, action));
            }
            if (action != null && isValidElement(action)) {
                return cloneElement(action, {
                    disabled,
                    size: this.computeSize().inputSize,
                });
            }
        };
        this.renderIcon = () => {
            var _a, _b, _c;
            const { icon } = this.props;
            // Icon passed as string
            if (typeof icon === 'string') {
                return (React.createElement(InputAddon, { onClick: () => this.focus() },
                    React.createElement(Icon, { name: icon, size: this.computeSize().iconSize })));
            }
            // Icon passed as React element
            if (icon != null && isValidElement(icon)) {
                const sizedIcon = cloneElement(icon, {
                    size: this.computeSize().iconSize,
                });
                const hasOnClick = typeof ((_b = (_a = icon) === null || _a === void 0 ? void 0 : _a.props) === null || _b === void 0 ? void 0 : _b.onClick) !== 'undefined';
                return (React.createElement(InputAddon, { onClick: () => !hasOnClick && this.focus() }, sizedIcon));
            }
            // Icon passed as Icon.props object
            if (icon != null && !isValidElement(icon)) {
                const hasOnClick = typeof ((_c = icon) === null || _c === void 0 ? void 0 : _c.onClick) !== 'undefined';
                return (React.createElement(InputAddon, { onClick: () => !hasOnClick && this.focus() },
                    React.createElement(Icon, Object.assign({}, icon))));
            }
            return null;
        };
        // Creating an input element
        this.renderInput = (htmlInputProps) => {
            const { input, type } = this.props;
            // Handling case of duplicate ID's for the same FormField label
            const isLabelAssigned = this.context.inputRef;
            const labelFor = this.context.labelFor;
            if (!isLabelAssigned) {
                this.context.inputRef = this.inputRef;
            }
            if (input != null && isValidElement(input)) {
                return cloneElement(input, Object.assign(Object.assign({}, htmlInputProps), { id: this.props.id || (!isLabelAssigned ? labelFor : undefined) }));
            }
            return createElement('input', Object.assign(Object.assign({}, htmlInputProps), { type, id: this.props.id || (!isLabelAssigned ? labelFor : undefined) }));
        };
        this.renderShortLabel = () => {
            var _a;
            const { shortLabel } = this.props;
            if (typeof shortLabel === 'string' && shortLabel !== '') {
                return (React.createElement(InputAddon, { onClick: () => this.focus() }, shortLabel));
            }
            // Label passed as React element
            if (shortLabel != null && isValidElement(shortLabel)) {
                const iconSize = shortLabel.props.size || this.computeSize().iconSize;
                const hasOnClick = typeof ((_a = shortLabel.props) === null || _a === void 0 ? void 0 : _a.onClick) !== 'undefined';
                return (React.createElement(InputAddon, { onClick: () => !hasOnClick && this.focus() }, cloneElement(shortLabel, { size: iconSize })));
            }
            return null;
        };
        // Handling with size-related shortcut props, like xsmall="true"...
        this.computeSize = () => {
            let inputSize = this.props.size;
            if (this.props.xsmall)
                inputSize = 'xsmall';
            if (this.props.small)
                inputSize = 'small';
            if (this.props.large)
                inputSize = 'large';
            const iconSize = {
                xsmall: '16px',
                small: '20px',
                medium: '20px',
                large: '24px',
            }[inputSize];
            return { inputSize, iconSize };
        };
        this.computeTabIndex = () => {
            const { disabled, tabIndex } = this.props;
            if (tabIndex != null) {
                return tabIndex;
            }
            if (disabled) {
                return -1;
            }
        };
        this.focus = () => this.inputRef.current.focus();
        this.select = () => this.inputRef.current.select();
        this.handleChange = (e) => {
            if (this.props.onChange) {
                this.props.onChange(e, Object.assign(Object.assign({}, this.props), { value: e.target.value }));
            }
        };
        this.handleChildOverrides = (child, defaultProps) => (Object.assign(Object.assign(Object.assign({}, defaultProps), child.props), { ref: (c) => {
                handleRef(child.ref, c);
                this.inputRef.current = c;
            } }));
    }
    render() {
        const { actionPosition, children, className, disabled, el: InputElement, error, fluid, focus, iconPosition, inverted, loading, shortLabelPosition, transparent, readonly, } = this.props;
        const _a = this.props, { inputRef } = _a, inputProps = __rest(_a, ["inputRef"]);
        const InputClasses = classnames('Input', className, {
            [`Input--${this.computeSize().inputSize}`]: this.computeSize().inputSize,
            'Input--disabled': disabled,
            'Input--error': error,
            'Input--fluid': fluid,
            'Input--focus': focus,
            'Input--inverted': inverted,
            'Input--loading': loading,
            'Input--transparent': transparent,
            'Input--readonly': readonly,
        });
        const [strictHtmlInputProps] = splitPropsUsingArray(inputProps, htmlInputPropsArray);
        const htmlInputProps = Object.assign(Object.assign({}, strictHtmlInputProps), { tabIndex: this.computeTabIndex(), onChange: this.handleChange, ref: this.inputRef });
        const [, rest] = splitPropsUsingArray(inputProps, [
            ...htmlInputPropsArray,
            ...inputPropsArray,
        ]);
        // Case of children passed directly into Input
        if (Children.count(children) !== 0) {
            const childElements = Children.toArray(children).map((child, k) => {
                if (isElementOfType(child, Button)) {
                    return child;
                }
                if (child.type === 'input') {
                    // Passing htmlInputProps to the <input />
                    return cloneElement(child, this.handleChildOverrides(child, htmlInputProps));
                }
                return React.createElement(Input.Addon, { key: k }, child);
            });
            return (React.createElement(InputElement, Object.assign({}, rest, { className: classnames(InputClasses, 'Input--with-children') }),
                React.createElement("div", { className: "Input__InnerWrapper" }, childElements)));
        }
        // Render with shorthands
        return (React.createElement(InputElement, Object.assign({ className: InputClasses, "data-anvil-component": "Input" }, rest),
            actionPosition === 'left' && this.renderAction(),
            React.createElement("div", { className: "Input__InnerWrapper" },
                iconPosition === 'left' && this.renderIcon(),
                shortLabelPosition === 'left' && this.renderShortLabel(),
                this.renderInput(htmlInputProps),
                loading && (React.createElement(InputAddon, { onClick: () => this.focus() },
                    React.createElement(Spinner, null))),
                shortLabelPosition === 'right' && this.renderShortLabel(),
                iconPosition === 'right' && this.renderIcon()),
            actionPosition === 'right' && this.renderAction()));
    }
}
Input.defaultProps = {
    actionPosition: 'right',
    el: 'div',
    iconPosition: 'right',
    shortLabelPosition: 'left',
    size: 'medium',
    type: 'text',
};
Input.Addon = InputAddon;
Input.contextType = FormFieldContext;
//# sourceMappingURL=Input.js.map